import React, { useCallback } from 'react'
import { useControlled } from '@grupoboticario/flora-react-hooks'
import { InputProps } from '@grupoboticario/flora-react-input'

function areEqualValues(a: object, b: object) {
  if (typeof b === 'object' && b !== null) {
    return a === b
  }

  return String(a) === String(b)
}

export type DropdownProps = InputProps & {
  multiple?: boolean
  native?: boolean
  readOnly?: boolean
  children?: React.ReactNode
  value?: any
  valueProp?: any
  defaultValue?: any
  variant?: string
  renderValue?: (selected: any) => React.ReactNode
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value?: any) => void
  onOpenChange?: (open: boolean) => void
  icon?: React.ReactNode
  disableNativeOnMobile?: boolean
}

type UseDropdownProps = Partial<DropdownProps>

const cloneEvent = (event: any, newValue: any) => {
  // Redefine target to allow name and value to be read.
  // This allows seamless integration with the most popular form libraries.
  // https://github.com/mui-org/material-ui/issues/13485#issuecomment-676048492
  // Clone the event to not override `target` of the original event.
  const nativeEvent = event.nativeEvent || event
  const clonedEvent = new nativeEvent.constructor(nativeEvent.type, nativeEvent)

  Object.defineProperty(clonedEvent, 'target', {
    writable: true,
    value: { value: newValue },
  })

  return clonedEvent
}

export function useDropdown<EventType>({
  multiple,
  native = false,
  children,
  valueProp,
  defaultValue = [],
  renderValue,
  onOpenChange,
  onChange,
  variant,
  readOnly = false,
}: UseDropdownProps) {
  const minWidth = React.useRef()

  const [value, setValueState] = useControlled({
    controlled: valueProp,
    default: defaultValue,
  })

  const [isOpen, setOpen] = React.useState(false)

  const inputSelectRef = React.useRef<HTMLInputElement>(null)

  const update = useCallback(
    (open: boolean, event?: any) => {
      setOpen((oldState) => {
        if (oldState === open) {
          return oldState
        }
        onOpenChange && onOpenChange(open)
        !open && inputSelectRef.current?.blur()
        return open
      })
    },
    [onOpenChange],
  )

  const handleItemClick = (child: React.ReactElement<any>) => (event: any) => {
    let newValue

    // We use the tabindex attribute to signal the available options.
    if (!event.currentTarget.hasAttribute('tabindex')) {
      return
    }

    // Suggested fix for an error in the Radix Checkbox
    // https://github.com/radix-ui/primitives/issues/2549#issuecomment-1859233467
    if (multiple && event.currentTarget !== event.target) {
      return
    }

    if (multiple) {
      newValue = Array.isArray(value) ? value.slice() : []
      const itemIndex = value?.indexOf?.(child.props.value)
      if (itemIndex === -1) {
        newValue.push(child.props.value)
      } else {
        newValue.splice(itemIndex, 1)
      }
    } else {
      newValue = child.props.value
    }

    if (child.props.onClick) {
      child.props.onClick(event)
    }

    if (value !== newValue) {
      setValueState(newValue)

      if (onChange) {
        const clonedEvent = cloneEvent(event, newValue)
        onChange(clonedEvent, child)
      }
    }

    if (!multiple) {
      update(false, event)
    }
  }

  const handleKeyDown = (child: React.ReactElement<any>) => (event: any) => {
    if (!readOnly && ['Enter', ' '].includes(event.key)) {
      event.preventDefault()
      handleItemClick(child)(event)
    }
  }

  const childrenArray = React.Children.toArray(children)

  let display: any
  let displaySingle: any
  let computeDisplay: any = false
  const displayMultiple: any = []

  if (value !== undefined) {
    if (renderValue) {
      display = renderValue(value)
    } else {
      computeDisplay = true
    }
  }

  const items = childrenArray.map((child) => {
    if (!React.isValidElement(child)) {
      return null
    }

    let selected: boolean

    if (multiple) {
      selected = value?.some?.((v: any) => areEqualValues(v, child.props.value))
      if (selected && computeDisplay) {
        displayMultiple.push(child.props.children)
      }
    } else {
      selected = areEqualValues(value, child.props.value)
      if (selected && computeDisplay) {
        displaySingle = child.props.children
      }
    }

    if (native) {
      return React.createElement(
        'option',
        {
          key: child.props.value,
          value: child.props.value,
          'aria-label': !Boolean(child.props.value) ? child.props.children : undefined, // if children empty, add aria-label value
          'aria-selected': selected ? 'true' : undefined,
          'data-value': child.props.value === '' ? ' ' : child.props.value, // Instead, we provide it as a data attribute.
          role: 'option',
          disabled: child.props.disabled,
        },
        Boolean(child.props.value) ? child.props.children : '', // If first element empty, set empty children
      )
    }

    return React.cloneElement(child as React.ReactElement<any>, {
      value: undefined, // The value is most likely not a valid HTML attribute.
      multiple,
      variant: variant,
      'data-value': child.props.value === '' ? ' ' : child.props.value, // Instead, we provide it as a data attribute.
      'aria-selected': selected ? 'true' : undefined,
      checked: selected,
      onClick: child.props.disabled ? () => {} : handleItemClick(child),
      onKeyDown: child.props.disabled ? () => {} : handleKeyDown(child),
      role: 'option',
    })
  })

  const handleMouseDown = (event: React.MouseEvent<EventType>) => {
    // Ignore everything but left-click
    if (event.button !== 0) return

    const inputEventWidth = (event.target as HTMLInputElement)?.clientWidth

    if (inputEventWidth !== minWidth.current) {
      ;(minWidth as any).current = inputEventWidth
    }

    // Hijack the default focus behavior.
    event.preventDefault()

    const setStateDropdownValue = (val: boolean) => {
      update(val)
    }

    !isOpen ? setStateDropdownValue(true) : setStateDropdownValue(false)
  }

  const handleChange = (event: React.ChangeEvent<any>) => {
    const index = childrenArray.map((child: any) => child.props.value).indexOf(event.target.value)

    const child = childrenArray[index] as React.ReactElement<any>

    if (!multiple) {
      // If not type of multiple select native, we only need to update the state.
      setValueState(event.target.value)
      onChange && onChange(event, child)
    } else {
      const { options } = event.target
      // If type of multiple select native, we need to update the state and the target value.
      const newValue = []
      for (let i = 0; i < options.length; i++) {
        if (options[i].selected) {
          newValue.push(options[i].value) // find selected options from native select
        }
      }
      setValueState(newValue)
      const clonedEvent = cloneEvent(event, newValue)
      onChange && onChange(clonedEvent, child)
    }
  }

  if (computeDisplay) {
    display = multiple ? displayMultiple.join(', ') : displaySingle
  }

  let filled = isOpen || display?.length ? true : false

  if (renderValue) {
    if (value.length === 0) filled = false
    else filled = true
  }

  return {
    value,
    filled,
    display,
    isOpen,
    update,
    handleMouseDown,
    handleChange,
    inputSelectRef,
    items,
    minWidth,
  }
}
